import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { ReactNode, useRef, createContext, useEffect } from "react";
import Card from "../../components/themed/Card";
import useModal from "../../hooks/useModal";

export interface ModalContextValue {
    dialogRef: React.RefObject<HTMLDialogElement | null>;
}

export const ModalContext = createContext<ModalContextValue | null>(null);

interface ModalProps {
    modalId: string;
    modalSize?: string;
    modalTitle: string;
    children: ReactNode;
    onClose?: () => void;
    openModal?: boolean;
}

const Modal = ({
    modalId,
    modalSize,
    modalTitle,
    children,
    onClose,
    openModal = false,
}: ModalProps) => {
    const dialogRef = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        const dialog = dialogRef.current;

        if(!dialog) return;

        if (!dialog.open && openModal)
            dialog.showModal();

        const handleClose = () => {
            if (onClose)
                onClose();
        };

        dialog.addEventListener("close", handleClose);

        return () => {
            dialog.removeEventListener("close", handleClose);
        };
    }, [ onClose, openModal ]);

    return (
        <ModalContext.Provider value= {{ dialogRef }}>
            <dialog ref={dialogRef} closedby="any" id={modalId} style={{ backgroundColor: "transparent" }}>
                <Card className={`${modalSize ?? "w-[60vw] h-[80vh]"} overflow-hidden`}>
                    <div className={`flex flex-row items-center justify-between border-b border-b-brand-500/10 dark:border-b-white/10 p-3 px-6 shadow-md shrink-0`}>
                        <h2 className={`text-lg font-bold w-[90%] truncate`}>{modalTitle}</h2>
                        <button type="button" onClick={() => dialogRef.current?.close()}>
                            <FontAwesomeIcon icon = {faXmark} size = "1x"  />
                        </button>
                    </div>
                    {children}
                </Card>
            </dialog>
        </ModalContext.Provider>
    );
};

interface ModalBodyProps {
    children: ReactNode;
}

Modal.Body = function ModalBody({ children } : ModalBodyProps){
    return (
        <div className={"flex-1 p-6 min-h-0 overflow-y-auto"}>
            {children}
        </div>
    );
};

interface ModalFooterProps {
    children?: ReactNode;
}

Modal.Footer = function ModalFooter({ children } : ModalFooterProps){
    const { dialogRef } = useModal();
    return (
        <div className={`flex flex-row justify-end gap-2 p-3 px-6 border-t border-t-brand-500/10 dark:border-t-white/10 w-full shrink-0`}>
            <button type="button" onClick={() => dialogRef.current?.close()} className={`text-blue-800 dark:text-white border-brand-500 p-2 rounded-md font-bold`}>Cancel</button>
            {children && children}
        </div>
    );
};

export default Modal;